import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { FixMeLater } from 'types'
import { useAppSelector } from 'app/hooks'
import useInfoModal from 'utils/hooks/useInfoModal'
import { BUTTONS, ERRORS, FORM, INFORMATION, MODALS, PLACEHOLDERS, TABS } from 'locales/locales'
import { useLazyGetUserByIdQuery, useUpdateUserByAdminMutation, useUpdateUserPasswordByAdminMutation } from 'app/services/user.service'

import UserInfo from './UserInfoForm'
import CustomModal from 'components/modals/CustomModal'
import UserStatistic from 'components/user-statistic/UserStatistics'
import CustomButton from 'components/button/CustomButton'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import { IRolePermissions, UserManagementPermissions } from 'types/role.types'
import { useLazyGetRolesQuery, useLazyGetUserRolesByAdminQuery } from 'app/services/system.service'
import { useLazyGetAdminNewsfeedTeamListQuery, useLazyGetUserTeamsByAdminQuery } from 'app/services/group.service'
import Loading from 'components/spinner/Loading'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { requiredValidation, confirmPasswordValidation } from 'utils/helpers/validations'
import { passwordValidator } from 'utils/helpers/passwordValidator'

interface IUserInfoProps {
  userId?: string
  onClose: () => void
  fetchUsers: () => void
  initialTabIndex?: number // -1 means "open password tab"
}
const initialUserState: {
  [key: string]: FixMeLater
} = {
  avatar: '',
  fullname: '',
  email: '',
  title: '',
  isDeleted: false,
  isAdmin: false,
}
const statisticDataInitial = {
  totalChannels: 0,
  totalMessages: 0,
  totalFiles: 0,
  totalVideoCall: 0,
}
const UserInfoModal = ({ userId, onClose, fetchUsers, initialTabIndex = 0 }: IUserInfoProps) => {
  const { t } = useTranslation()
  const { openModal, closeModal, Modal } = useInfoModal()
  const [formChecked, setFormChecked] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const [selectedUserData, setSelectedUserData] = useState(initialUserState)
  const [userStatisticData, setUserStatisticData] = useState(statisticDataInitial)
  const [dropDownData, setDropDownData] = useState<{
    teams?: Array<FixMeLater>
    roles?: Array<FixMeLater>
  }>({roles:[], teams:[]})

  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    newPasswordAgain: '',
  })

  type Option = { value: string; title: string };

  const [selectedOptions, setSelectedOptions] = useState<{ roleId: Option[]; teamId: Option[] }>({
    roleId: [],
    teamId: [],
  });

  const [updateUserByAdmin] = useUpdateUserByAdminMutation()
  const [updateUserPasswordByAdmin] = useUpdateUserPasswordByAdminMutation()
  const [getUserById] = useLazyGetUserByIdQuery()
  const [getAdminNewsfeedTeamList] = useLazyGetAdminNewsfeedTeamListQuery()
  const [getRoles] = useLazyGetRolesQuery({refetchOnReconnect: true})
  const [getUserTeams] = useLazyGetUserTeamsByAdminQuery()
  const [getUserRolesByAdmin] = useLazyGetUserRolesByAdminQuery()

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)
  const passwordSettingsSlice = useAppSelector((state) => state.system.passwordSettings)

  const hasUserViewStatPermission = isFlagSetIn([UserManagementPermissions.VIEW_USER_STATISTICS], permissions.userManagement)


  const setDropdownDataState = useCallback((filter: FixMeLater) => {
    // Bu fonksiyon dropdown search için gerekli ama burada kullanmıyoruz
    console.log('Filter state updated:', filter)
  }, [])


  useEffect(() => {
    let mounted = true;
  
    const loadInitialDropdownData = async () => {
      try {
        setIsLoading(true);
  
        const [teams, roles] = await Promise.all([
          getAdminNewsfeedTeamList({ skip: 0, take: 100, sort: [], filter: { logic: 'and', filters: [] } }),
          getRoles({ take: 999, skip: 0, sort: -1, count: 0 }),
        ]);
  
        if (mounted && roles?.data?.data) {
          setDropDownData((prev) => ({
            ...prev,
            roles: roles.data.data.map((r: FixMeLater) => ({ value: r._id, title: r.name })),
          }));
        }
  
        if (mounted && teams?.data?.data) {
          setDropDownData((prev) => ({
            ...prev,
            teams: teams.data.data.map((t: FixMeLater) => ({ value: t._id, title: t.name })),
          }));
        }
  
        if (mounted) await fetchUserInfo();
      } catch (e) {
        console.error('Error loading initial data:', e);
        onClose();
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
  
    if (userId) loadInitialDropdownData();
    return () => {
      mounted = false;
    };
  }, [userId]);
  

  const handleDropdownSearch = useCallback(async (skip: number, filter: string) => {
    try {
      const filterConfig = filter
        ? { logic: 'and', filters: [{ field: 'name', operator: 'contains', value: filter }] }
        : { logic: 'and', filters: [] }

      const adminNewsfeedTeamList = (await getAdminNewsfeedTeamList({
        skip,
        take: 100,
        filter: filterConfig,
      })) as FixMeLater
      if (adminNewsfeedTeamList?.data?.success) {
        const mappedData = adminNewsfeedTeamList.data.data.map((team: FixMeLater) => ({
          title: team.name,
          value: team._id,
        }))
        
        return {
          data: mappedData,
          total: adminNewsfeedTeamList.data.totalCount || mappedData.length,
        }
      }  
      return { data: [], total: 0 }
    } catch (error) {
      console.error('Error in handleDropdownSearch:', error)
      return { data: [], total: 0 }
    }
  }, [getAdminNewsfeedTeamList])

  const fetchUserInfo = async () => {
    const [userInfo, userTeams, userRoles] = await Promise.all([
      getUserById({ id: userId }),
      getUserTeams({ userId }),
      getUserRolesByAdmin({ userId }),
    ]);

    setSelectedUserData(userInfo.data.data.info);
    setUserStatisticData(userInfo.data.data.statistic);
  
    setSelectedOptions({
      teamId: (userTeams.data?.data ?? []).map((team: FixMeLater) => ({ value: team._id, title: team.name })),
      roleId: (userRoles.data?.data ?? []).map((role: FixMeLater) => ({ value: role._id, title: role.name })),
    });
  
    return userInfo.data.data;
  };

  const onConfirmOk = async () => {
    setFormChecked(true)
    try {
      setIsLoading(true)
      await updateUserByAdmin({
        userId,
        teams: selectedOptions?.teamId?.map((team: FixMeLater) => team.value) || [],
        roles: selectedOptions?.roleId?.map((role: FixMeLater) => role.value) || [],
      })
      fetchUsers()
    } catch (error) {
      console.error('Error in onConfirmOk:', error)

    } finally {
      setFormChecked(false)
      setIsLoading(false)
      handleClose()
    }
  }

  const onConfirmCancel = () => {
    closeModal()
  }

  const onOk = () => {
    openModal({
      title: t(MODALS.UPDATE_USER_TITLE),
      description: t(MODALS.UPDATE_USER_DESCRIPTION),
      cancelTitle: t(BUTTONS.CANCEL),
      okTitle: t(BUTTONS.YES),
      onCancel: onConfirmCancel,
      onOk: onConfirmOk,
    })
  }

const onChangeInput = useCallback((value: any, key: string) => {
  setSelectedOptions((prev) => ({ ...prev, [key]: value }));
  setSelectedUserData((prev) => ({ ...prev, [key]: value }));
}, []);

  const onChangePasswordInput = useCallback((key: 'newPassword' | 'newPasswordAgain', value: string) => {
    setPasswordForm((prev) => ({ ...prev, [key]: value }))
  }, [])

  const onChangePassword = useCallback(async () => {
    setFormChecked(true)
    
    // Validation
    const { isValid: isRequiredNew } = requiredValidation(passwordForm.newPassword)
    const { isValid: isRequiredConfirm } = requiredValidation(passwordForm.newPasswordAgain)
    const { isValid: isConfirm } = confirmPasswordValidation(
      passwordForm.newPassword,
      passwordForm.newPasswordAgain,
    )

    if (!isRequiredNew || !isRequiredConfirm || !isConfirm) {
      return
    }

    // Password settings validation
    if (passwordSettingsSlice && Object.keys(passwordSettingsSlice).length > 0) {
      const validate = passwordValidator(passwordForm.newPassword, passwordSettingsSlice)
      if (!validate.valid) {
        DropdownAlert.ref.show({
          isError: true,
          message: validate.message || t(ERRORS.INVALID_PASSWORD),
        })
        return
      }
    }

    try {
      setIsLoading(true)
      const result: FixMeLater = await updateUserPasswordByAdmin({
        userId,
        password: passwordForm.newPassword,
      })

      if (result?.data?.success) {
        DropdownAlert.ref.show({
          isError: false,
          message: t(INFORMATION.USER_PASSWORD_UPDATED as any) || 'Password updated successfully',
        })
        // Reset password form
        setPasswordForm({ newPassword: '', newPasswordAgain: '' })
        setFormChecked(false)
      } else {
     
        DropdownAlert.ref.show({
          isError: true,
          message: result.error?.data?.message || t(ERRORS.SOMETHING_WENT_WRONG_ERROR),
        })
      }
    } catch (error: any) {
      console.error('Error updating user password:', error)

      DropdownAlert.ref.show({
        isError: true,
        message: error?.response?.data?.message || t(ERRORS.SOMETHING_WENT_WRONG_ERROR),
      })
    } finally {
      setIsLoading(false)
      setFormChecked(false)
    }
  }, [passwordForm, userId, updateUserPasswordByAdmin, passwordSettingsSlice, t])

  const modalTabs: FixMeLater = [{ title: t(TABS.GENERAL), action: true }]

  if (hasUserViewStatPermission) {
    modalTabs.push({ title: t(TABS.STATISTIC) })
  }
  
  modalTabs.push({ title: t(TABS.PASSWORD) })

  // Calculate password tab index: General (0) + Statistic (1 if exists) = password tab index
  const passwordTabIndex = hasUserViewStatPermission ? 2 : 1
  const calculatedInitialTabIndex = initialTabIndex === -1 ? passwordTabIndex : (initialTabIndex || 0)
 
  const handleClose = () => {
    onClose()
    setSelectedUserData(initialUserState)
    closeModal()
  }
  return (
    <>
      {isLoading && <Loading />}
      <CustomModal
        closeModal={onClose}
        onCancel={onClose}
        onOk={onOk}
        title={t(MODALS.USER_INFO_TITLE)}
        tab={modalTabs}
        showButtons={false}
        loading={isLoading}
        initialTabIndex={calculatedInitialTabIndex}
      >
        <UserInfo
          id={selectedUserData._id}
          avatar={selectedUserData.avatar}
          fullname={selectedUserData.fullname}
          email={selectedUserData.email}
          title={selectedUserData.title}
          isDeleted={selectedUserData.isDeleted}
          formChecked={formChecked}
          onChange={onChangeInput}
          setDropDownData={setDropDownData}
          selectedOptions={selectedOptions}
          onChangeInput={onChangeInput}
          setDropdownDataState={setDropdownDataState}
          handleDropdownSearch={handleDropdownSearch}
          dropDownData={dropDownData}
        />
        {hasUserViewStatPermission && <UserStatistic {...userStatisticData} />}
        <PasswordTab
          newPassword={passwordForm.newPassword}
          newPasswordAgain={passwordForm.newPasswordAgain}
          onChange={onChangePasswordInput}
          onSubmit={onChangePassword}
          t={t}
        />
      </CustomModal>
      {Modal}
    </>
  )
}

export default UserInfoModal

const PasswordTab = ({ newPassword, newPasswordAgain, onChange, onSubmit, t }: FixMeLater) => {
  return (
    <PasswordWrapper>
      <Field>
        <Label>{t(FORM.NEW_PASSWORD)}</Label>
        <Input
          type="password"
          value={newPassword}
          onChange={(e) => onChange('newPassword', e.target.value)}
          placeholder={t(PLACEHOLDERS.NEW_PASSWORD) || 'Enter new password'}
          autoComplete="new-password"
        />
      </Field>
      <Field>
        <Label>{t(FORM.NEW_PASSWORD_AGAIN)}</Label>
        <Input
          type="password"
          value={newPasswordAgain}
          onChange={(e) => onChange('newPasswordAgain', e.target.value)}
          placeholder={t(PLACEHOLDERS.NEW_PASSWORD_AGAIN) || 'Enter again'}
          autoComplete="new-password"
        />
      </Field>
      <Actions>
        <ChangeButton
          text={t(BUTTONS.CHANGE_PASSWORD) || 'Change'}
          textColor="white"
          onClick={onSubmit}
        />
      </Actions>
    </PasswordWrapper>
  )
}

const PasswordWrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 420px;
`

const Label = styled.div`
  font-size: 13px;
  color: #333;
  font-weight: 500;
`

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #129fdb;
    box-shadow: 0 0 0 2px rgba(18, 159, 219, 0.12);
  }
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 6px;
`

const ChangeButton = styled(CustomButton)`
  background-color: #1e4ab7;
  border-radius: 6px;
  font-weight: 500;
  padding: 8px 16px;
  height: 34px;
`
